import { Wallet, Activity, Gauge, Receipt } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import StatsSection, { type MetricItem } from '@/pages/Dashboard/StatsSection'
import { renderQuotaCompact, formatNumberCompact } from '@/utils/quota'

interface StatsCardsProps {
  balance: number
  todayRequests: number
  todayQuota: number
  todayLoading?: boolean
  realtimeRpm?: number
  realtimeRpmLoading?: boolean
  realtimeRpmError?: boolean
}

function formatRpmValue(
  rpm: number | undefined,
  loading: boolean | undefined,
  error: boolean | undefined
): string {
  if (loading) return ''
  if (error) return '--'
  if (rpm == null) return '--'
  return formatNumberCompact(rpm)
}

/**
 * Dashboard 首屏四张核心指标卡：余额、今日请求、实时 RPM、今日消耗。
 */
export function StatsCards({
  balance,
  todayRequests,
  todayQuota,
  todayLoading,
  realtimeRpm,
  realtimeRpmLoading,
  realtimeRpmError,
}: StatsCardsProps) {
  const { t } = useTranslation()

  const rpmMeta =
    realtimeRpmError && !realtimeRpmLoading
      ? t('tt.dashboard.rpmUnavailable')
      : t('tt.dashboard.last60Seconds')

  const metrics: MetricItem[] = [
    {
      key: 'balance',
      title: t('tt.dashboard.balance'),
      value: renderQuotaCompact(balance),
      icon: Wallet,
      tone: 'indigo',
    },
    {
      key: 'today-requests',
      title: t('tt.dashboard.todayRequests'),
      value: formatNumberCompact(todayRequests),
      icon: Activity,
      tone: 'cyan',
      loading: todayLoading,
    },
    {
      key: 'realtime-rpm',
      title: t('tt.dashboard.realtimeRpm'),
      value: formatRpmValue(realtimeRpm, realtimeRpmLoading, realtimeRpmError),
      hint: rpmMeta,
      icon: Gauge,
      tone: 'violet',
      loading: realtimeRpmLoading,
    },
    {
      key: 'today-cost',
      title: t('tt.dashboard.todayCost'),
      value: renderQuotaCompact(todayQuota),
      icon: Receipt,
      tone: 'amber',
      loading: todayLoading,
    },
  ]

  return <StatsSection metrics={metrics} />
}
